import {
  getAttributionValue,
  getSealedLandingValues,
  isUsableAttributionValue,
} from "./attribution";

/**
 * Handoff from the marketing site (life5.xx) to the customer app (app.life5.xx).
 *
 * The first-party cookies are written on the registrable domain (.life5.xx)
 * and are therefore visible to the app, but the CookieYes CMP deletes the
 * attribution cookies while consent is pending, and the app cannot tell a
 * deleted cookie from a genuine direct landing. Carrying the attribution in
 * the query string makes the handoff independent of consent and of cookie
 * timing. The app reads these params on arrival:
 * `customerinterface/src/utils/clickIdUrl.ts` (gclid / gbraid / wbraid) and
 * `GetLifeContext.context.tsx` (utm_*, tid, click ids, entryPage,
 * referrerUrl) — the names below must stay in sync with those two files.
 *
 * Every value is read through `getAttributionValue`, which falls back to the
 * sessionStorage seal when the cookie is gone, so gclid/gbraid/wbraid/msclkid
 * and the utm_* set travel even for a visitor who has not answered the banner.
 */

const ATTRIBUTION_COOKIES = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_test",
  "tid",
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "ucc",
  "entryPage",
  "referrerUrl",
];

// Full urls can be arbitrarily long; keep the handoff url within sane limits.
const MAX_URL_VALUE_LENGTH = 1000;
const TRUNCATED_COOKIES = ["entryPage", "referrerUrl"];

// Hosts the handoff decoration applies to. `GATSBY_DOMAIN_URL` is the market
// domain (life5.es / life5.fr / life5.it / life5.pt, sometimes with a leading
// dot because it is also used as the cookie Domain attribute).
const STATIC_APP_HOSTS = ["app.life5.es", "app.axa.life5.es"];

const isUsableValue = isUsableAttributionValue;

export const getAppHandoffHosts = (): string[] => {
  const hosts = STATIC_APP_HOSTS.slice();
  const domain = (process.env.GATSBY_DOMAIN_URL || "")
    .trim()
    .replace(/^\./, "")
    .toLowerCase();
  if (domain) {
    [`app.${domain}`, `app.axa.${domain}`].forEach((host) => {
      if (!hosts.includes(host)) hosts.push(host);
    });
  }
  return hosts;
};

export const isAppHandoffHost = (hostname: string | null | undefined): boolean =>
  !!hostname && getAppHandoffHosts().includes(hostname.toLowerCase());

/** True for an absolute http(s) url pointing at one of our app subdomains. */
export const isAppHandoffUrl = (href: string | null | undefined): boolean => {
  if (!href) return false;
  try {
    const url = new URL(
      href,
      typeof window !== "undefined" ? window.location.href : "https://life5.es/"
    );
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    return isAppHandoffHost(url.hostname);
  } catch (error) {
    return false;
  }
};

export const buildAppHandoffUrl = (
  baseUrl: string,
  extra: Record<string, string | null | undefined>
): string => {
  if (!baseUrl) return baseUrl;

  try {
    const url = new URL(
      baseUrl,
      typeof window !== "undefined" ? window.location.href : undefined
    );
    const params = url.searchParams;

    Object.entries(extra).forEach(([key, value]) => {
      if (!isUsableValue(value)) return;
      params.set(key, value);
    });

    const sealed = getSealedLandingValues();
    ATTRIBUTION_COOKIES.forEach((name) => {
      // Explicit params (and whatever baseUrl already carried) win — this is
      // also what makes a second decoration of the same anchor a no-op.
      if (params.has(name)) return;
      // Cookie first, sessionStorage seal when the CMP has removed it.
      let value = getAttributionValue(name);
      if (!isUsableValue(value) && name === "entryPage") value = sealed.entryPage;
      if (!isUsableValue(value) && name === "referrerUrl")
        value = sealed.referrerUrl;
      // Never append an empty / placeholder value: the app treats a present
      // param as authoritative and would overwrite a good cookie with "".
      if (!isUsableValue(value)) return;
      params.set(
        name,
        TRUNCATED_COOKIES.includes(name)
          ? value.slice(0, MAX_URL_VALUE_LENGTH)
          : value
      );
    });

    return url.toString();
  } catch (error) {
    // Never block the handoff because of an unparseable url.
    return baseUrl;
  }
};

/**
 * Rewrites an anchor's href in place when it points at the app. Idempotent:
 * `buildAppHandoffUrl` skips params the url already carries, so decorating
 * twice cannot double-append. Returns true when the href changed.
 */
export const decorateAppAnchor = (
  anchor: HTMLAnchorElement | null | undefined
): boolean => {
  if (!anchor) return false;
  const current = anchor.getAttribute("href");
  if (!isAppHandoffUrl(current)) return false;
  const decorated = buildAppHandoffUrl(anchor.href || current, {});
  if (!decorated || decorated === anchor.href) return false;
  anchor.setAttribute("href", decorated);
  return true;
};

let appLinkInterceptorInstalled = false;

const handleAppLinkActivation = (event: Event) => {
  // A handler that already took over the navigation (React router, analytics)
  // wins; we only ever rewrite the href, never the navigation itself.
  if (event.defaultPrevented) return;
  const target = event.target as Element | null;
  if (!target || typeof (target as any).closest !== "function") return;
  const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
  if (!anchor) return;
  // Decoration is a plain attribute rewrite, so `target="_blank"`, middle
  // clicks and cmd/ctrl clicks keep their native behaviour AND carry the
  // attribution into the new tab.
  decorateAppAnchor(anchor);
};

/**
 * Safety net for the CTAs this codebase does not own: Strapi `basic.html`
 * blocks and any hardcoded `<a href="https://app.axa.life5.es/calculator/1">`
 * are never decorated at render time, and that hop used to rely 100 % on
 * cookies. Capture phase so the href is already rewritten by the time the
 * click reaches React or the GA4 cross-domain linker.
 */
export const installAppLinkInterceptor = (): void => {
  if (typeof document === "undefined") return;
  if (appLinkInterceptorInstalled) return;
  appLinkInterceptorInstalled = true;

  document.addEventListener("click", handleAppLinkActivation, true);
  // Middle click / some "open in new tab" paths fire auxclick, not click.
  document.addEventListener("auxclick", handleAppLinkActivation, true);
  // Keyboard activation of a link fires `click`, but a long press on mobile
  // ("open in new tab" from the context menu) fires neither.
  document.addEventListener("contextmenu", handleAppLinkActivation, true);
};

export const navigateToApp = (url: string): void => {
  if (typeof window === "undefined") return;

  try {
    // The GA4 cross domain linker only decorates REAL anchor clicks (it hooks
    // the click event to append `_gl`). Assigning `window.location.href` skips
    // it, so the app domain starts a brand new session and the conversion is
    // attributed to a referral from the marketing site.
    const link = document.createElement("a");
    link.href = url;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    window.location.href = url;
  }
};
